import { Application } from 'pixi.js';
import { OverlayId } from '@trifecta/slot-games-sdk/dist/trifecta/framework/ui/overlays/OverlayId';
import { SIMPLE_THEME } from '@trifecta/slot-games-sdk/dist/trifecta/framework/ui/themes/Themes';
import { TrifectaSDK } from '@trifecta/slot-games-sdk/dist/TrifectaSDK';
import { Theme } from '@trifecta/slot-games-sdk/dist/trifecta/framework/ui/themes/Theme';
import { LoadingOverlay } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/ui/overlays/LoadingOverlay';
import { Config } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/Config';
import { Game } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/Game';
import { AutoPlayRuleValue } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/AutoPlayRuleValue';
import { IOverlay } from '@trifecta/slot-games-sdk/dist/trifecta/framework/ui/overlays/IOverlay';
import { IGameHook } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/IGameHook';
import { PopUpId } from '@trifecta/slot-games-sdk/dist/trifecta/framework/ui/popups/PopUpId';
import { LogLevel } from '@trifecta/slot-games-sdk/dist/trifecta/framework/logger/LogLevel';
import { SpinType } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/IGameHook';
import { Compatibility, Container, Platform, T_Platform } from '@cgs/wge';
import { LOCALIZATION_MANAGER_TOKEN, LocalizationManager } from '@cgs/shared';
import {
  TRIFECTA_LAUNCH_PARAMS_TOKEN,
  TrifectaLaunchParamsDto,
  TrifectaAckResultResponse,
} from '../models/trifecta-dto';
import { TrifectaAutoplayRules } from '../constants/trifecta-autoplay.constants';

export type { LaunchParams as TrifectaSDKLaunchParams } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/LaunchParams';
export { TurboSpinMode as TrifectaSDKTurboSpinMode } from '@trifecta/slot-games-sdk/dist/trifecta/sdk/ui/components/IUiControlPanel';

export const TRIFECTA_SDK_ADAPTER_TOKEN = Symbol('TRIFECTA_SDK_ADAPTER_TOKEN');

export enum TrifectaSDKErrorCode {
  InsufficientFunds = 'INSUFFICIENT_FUNDS',
}

const MIN_DPR: number = 1;
const MAX_DPR: number = 3;

export type TrifectaSDKGameHook = IGameHook;
export type TrifectaSDKLoadingOverlay = LoadingOverlay;
export type TrifectaSDKLogLevel = LogLevel;
export type TrifectaSDKSpinType = SpinType;
export type TrifectaSDKConfig = Config;

export interface ITrifectaSDKAdapterParams {
  gameTitle: string;
  gameVersion: string;
  hasIntro: boolean;
  hasBuyFeatures: boolean;
  enableAutoReplay: boolean;
  isDebug?: boolean;
  debugLabelText?: string;
  uiAtlasId?: string;
  theme?: Theme;
}

// TODO: check all "TODO: trifecta sdk"
export class TrifectaSDKAdapter {
  public readonly launchParams: TrifectaLaunchParamsDto;

  private readonly gameCanvas: HTMLCanvasElement;
  private readonly localizationManager: LocalizationManager | null;

  private initPromise: Promise<void> | null = null;
  private container: HTMLDivElement | null = null;

  constructor(
    container: Container,
    private readonly params: ITrifectaSDKAdapterParams
  ) {
    this.gameCanvas = container.resolve<Platform>(T_Platform).view.canvas;
    this.launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);
    this.localizationManager = container.resolve<LocalizationManager>(LOCALIZATION_MANAGER_TOKEN, {
      optional: true,
    });
  }

  public async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.startSDK();
    return this.initPromise;
  }

  private async startSDK(): Promise<void> {
    const dpr = Math.min(Math.max(Math.round(window.devicePixelRatio || 1), MIN_DPR), MAX_DPR);

    const sdkApplication = new Application();
    await sdkApplication.init({
      resizeTo: window,
      resolution: dpr,
      autoDensity: true,
      background: 0x000000,
      backgroundAlpha: 0,
      preserveDrawingBuffer: true,
    });

    this.container = this.createSDKContainer();
    this.container.appendChild(sdkApplication.canvas);

    const mouseTypes = [
      'mousedown',
      'mouseup',
      'mousemove',
      'wheel',
      'mouseleave',
      'mouseenter',
      'click',
      'dblclick',
      'contextmenu',
    ];

    const sdkCanvas = sdkApplication.canvas as HTMLCanvasElement;
    const gl = sdkCanvas.getContext('webgl2') ?? sdkCanvas.getContext('webgl');

    // Returns true when the SDK has rendered UI at (clientX, clientY).
    // preserveDrawingBuffer:true keeps the last frame readable via readPixels.
    const isSdkUiAt = (clientX: number, clientY: number): boolean => {
      if (!gl) return false;
      const rect = sdkCanvas.getBoundingClientRect();
      const x = Math.floor((clientX - rect.left) * dpr);
      const y = Math.floor((clientY - rect.top) * dpr);
      const pixel = new Uint8Array(4);
      gl.readPixels(x, gl.drawingBufferHeight - y - 1, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
      return pixel[3] > 0;
    };

    const forwardMouse = (e: any) => {
      // mousedown aimed at an SDK UI element must not reach game buttons.
      // mouseup without a prior forwarded mousedown is harmless: Mouse._onMouseUp
      // guards with `if (this._downEvent)` so no spurious clicks are fired.
      if (e.type === 'mousedown' && isSdkUiAt(e.clientX, e.clientY)) return;

      // wheel — отдельный класс события
      const ev =
        e.type === 'wheel'
          ? new WheelEvent('wheel', {
              bubbles: true,
              cancelable: true,
              clientX: e.clientX,
              clientY: e.clientY,
              deltaX: e.deltaX,
              deltaY: e.deltaY,
              deltaZ: e.deltaZ,
              deltaMode: e.deltaMode,
              ctrlKey: e.ctrlKey,
              shiftKey: e.shiftKey,
              altKey: e.altKey,
              metaKey: e.metaKey,
            })
          : new MouseEvent(e.type, {
              bubbles: true,
              cancelable: true,
              clientX: e.clientX,
              clientY: e.clientY,
              button: e.button,
              buttons: e.buttons,
              ctrlKey: e.ctrlKey,
              shiftKey: e.shiftKey,
              altKey: e.altKey,
              metaKey: e.metaKey,
            });

      this.gameCanvas.dispatchEvent(ev);
    };

    mouseTypes.forEach((t) =>
      sdkApplication.canvas.addEventListener(t, forwardMouse, { passive: false })
    );

    // const gl = sdkApplication.canvas.getContext('webgl2');
    // sdkApplication.canvas.addEventListener('pointerdown', (e) => {
    //   const rect = sdkApplication.canvas.getBoundingClientRect();
    //
    //   const x = Math.floor(e.clientX - rect.left) * dpr;
    //   const y = Math.floor(e.clientY - rect.top) * dpr;
    //
    //   const flippedY = gl!.drawingBufferHeight - y - 1;
    //
    //   const pixels = new Uint8Array(4);
    //
    //   gl!.readPixels(x, flippedY, 1, 1, gl!.RGBA, gl!.UNSIGNED_BYTE, pixels);
    //
    //   if (pixels[3] === 0) {
    //     const newEvent = new PointerEvent(e.type, {
    //       pointerId: e.pointerId,
    //       bubbles: e.bubbles,
    //       cancelable: e.cancelable,
    //       clientX: e.clientX,
    //       clientY: e.clientY,
    //       button: e.button,
    //       buttons: e.buttons,
    //       pointerType: e.pointerType,
    //       isPrimary: e.isPrimary,
    //     });
    //
    //     this.gameCanvas.dispatchEvent(newEvent);
    //   }
    // });

    await TrifectaSDK.init(sdkApplication, {
      launchParams: this.launchParams,
      isMobile: Compatibility.IsMobileBrowser,
      hasIntro: this.params.hasIntro,
      hasBuyFeatures: this.params.hasBuyFeatures,
      enableAutoReplay: this.params.enableAutoReplay,
      gameTitle: this.params.gameTitle,
      gameVersion: this.params.gameVersion,
      uiAtlasId: this.params.uiAtlasId || 'default_0',
      theme: this.params.theme || SIMPLE_THEME,
      autoPlayRules: [
        {
          id: TrifectaAutoplayRules.StopOnAnyWin,
          type: 'toggle',
          defaultIsActive: false,
          getCaption: () => {
            const defaultText = 'On Any Win';
            return (
              this.localizationManager?.get('autoplay_rule_on_any_win', defaultText) || defaultText
            );
          },
        },
        {
          id: TrifectaAutoplayRules.StopOnBonusWin,
          type: 'toggle',
          defaultIsActive: false,
          getCaption: () => {
            const defaultText = 'On Bonus Win';
            return (
              this.localizationManager?.get('autoplay_rule_on_bonus_win', defaultText) ||
              defaultText
            );
          },
        },
      ],
      isDebug: this.params.isDebug || false,
      hasUnifiedSoundSettings: true,
    });

    if (this.params.isDebug) {
      this.setLogLevel('verbose');
    }

    this.createPopUpContainer();
  }

  public showLoadingOverlay(): Promise<LoadingOverlay> {
    return TrifectaSDK.showOverlay(OverlayId.LOADING) as Promise<LoadingOverlay>;
  }

  public showGameOverlay(): Promise<IOverlay | null> {
    return TrifectaSDK.showOverlay(OverlayId.GAME);
  }

  public showContainer(): void {
    this.toggleContainer(true);
  }

  public hideContainer(): void {
    this.toggleContainer(false);
  }

  public toggleContainer(show: boolean): void {
    if (this.container) {
      this.container.style.display = show ? 'block' : 'none';
    }
  }

  public setGameHook(gameHook: TrifectaSDKGameHook): void {
    TrifectaSDK.setGameHook(gameHook);
  }

  public buyFeature(bet: number, buyFeatureId: string = 'buyFeature', args?: any): Promise<void> {
    return TrifectaSDK.buyFeature(bet, buyFeatureId, args);
  }

  public gamble(type: string, multiplier: number, clientData?: any): Promise<void> {
    return TrifectaSDK.gamble(type, multiplier, clientData);
  }

  public addWin(amount: number): void {
    TrifectaSDK.addWinning(amount);
  }

  public abortAutoPlay(): void {
    TrifectaSDK.abortAutoPlay();
  }

  public getAutoPlayRuleValue(ruleId: string): AutoPlayRuleValue | null {
    return TrifectaSDK.getAutoPlayRuleValue(ruleId) || null;
  }

  public hideControlPanel(): void {
    TrifectaSDK.hideControlPanel();
  }

  public getBet(): number {
    return TrifectaSDK.getBet();
  }

  public setDebug(isDebug: boolean): void {
    TrifectaSDK.setDebug(isDebug);
  }

  public setLogLevel(level: TrifectaSDKLogLevel): void {
    TrifectaSDK.setLogLevel(level);
  }

  public setBet(bet: number): void {
    TrifectaSDK.setBet(bet);
  }

  public showControlPanel(): void {
    TrifectaSDK.showControlPanel();
  }

  public showFreeSpins(): void {
    TrifectaSDK.showFreeSpins();
  }

  public hideFreeSpins(): void {
    TrifectaSDK.hideFreeSpins();
  }

  public incrementFreeSpins(amount: number, duration = 500, onCompleted = () => {}): void {
    TrifectaSDK.incrementFreeSpins(amount, duration, onCompleted);
  }

  public decrementFreeSpins(amount: number, duration = 500, onCompleted = () => {}): void {
    TrifectaSDK.decrementFreeSpins(amount, duration, onCompleted);
  }

  public get gambleValue(): number {
    return Game.instance.gambleValue || 0;
  }

  public get isSoundOn(): boolean {
    return Config.instance.isSoundOn;
  }

  public onSoundChange(callback: (isSoundOn: boolean) => void): () => void {
    return this.onConfigChanged('isSoundOn', callback);
  }

  public get skipIntro(): boolean {
    return Config.instance.skipIntro;
  }

  public get isReplay(): boolean {
    return !!this.launchParams.replayData;
  }

  public get config(): TrifectaSDKConfig {
    return Config.instance;
  }

  public onSkipIntroChange(callback: (skipIntro: boolean) => void): () => void {
    return this.onConfigChanged('skipIntro', callback);
  }

  public get turboSpinMode(): number {
    return Config.instance.turboSpinMode;
  }

  public onTurboSpinChange(callback: (isTurboSpin: boolean) => void): () => void {
    return this.onConfigChanged('turboSpinMode', callback);
  }

  public acknowledgeSpins(
    skip: number = 1,
    clientData?: any
  ): Promise<TrifectaAckResultResponse | null> {
    return TrifectaSDK.acknowledgeSpins(skip, clientData) ?? Promise.resolve(null);
  }

  public closeAllPopUps(): void {
    void TrifectaSDK.closeAllPopUps();
  }

  public showSettingsPopup(): void {
    void TrifectaSDK.showPopUp(PopUpId.SETTINGS);
  }

  public setCanvasSize(width: number, height: number): void {
    TrifectaSDK.setSize(width, height);
  }

  private onConfigChanged(param: keyof Config, callback: (isSoundOn: boolean) => void): () => void {
    const eventName = 'changed';
    const config = Config.instance;
    let currentParamState = config[param];
    Config.instance.on(eventName, () => {
      if (currentParamState !== config[param]) {
        currentParamState = config[param];
        callback(currentParamState);
      }
    });
    return () => Config.instance.removeListener(eventName, callback);
  }

  private createSDKContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'sdk-container';

    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.zIndex = '10';

    document.body.appendChild(container);
    return container;
  }

  private createPopUpContainer(): void {
    const popUpContainer = document.createElement('div');
    popUpContainer.id = 'popup-container';
    popUpContainer.style.display = 'none';
    popUpContainer.appendChild(this.createPopUp());

    document.getElementById('fullscreen-container')?.appendChild(popUpContainer);
  }

  private createPopUp(): HTMLDivElement {
    const doc = window.document;

    const popUp = doc.createElement('div');
    popUp.id = 'popup';
    popUp.innerHTML = `
            <span id='game-title'></span>
            <div id='popup-inner-container'>
                <div id="popup-header">
                    <h1></h1>
                    <button id="close-button">
                        <svg id="close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.45 19.45">
                            <g id="close_button" data-name="close_button">
                                <path fill="#ffffff" id="button_close" d="M9.72,11.49l-7.95,7.96-1.77-1.77,7.95-7.96L0,1.77,1.77,0l7.95,7.95L17.68,0l1.77,1.77-7.96,7.95,7.95,7.96-1.77,1.77-7.95-7.96Z" />
                            </g>
                        </svg>
                    </button>
                </div>
                <iframe id="popup-body" allowfullscreen scrolling="yes" sandbox="allow-scripts allow-same-origin"></iframe>
            </div>
        `;

    return popUp;
  }
}
